/**
 * Create Metadata Page
 * Standalone page for creating and uploading token metadata to IPFS
 */
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MetadataBuilder from '../components/MetadataBuilder';

export const CreateMetadataPage: React.FC = () => {
  const navigate = useNavigate();
  const [metadataUri, setMetadataUri] = useState<string>('');

  const handleComplete = (metadata: any, ipfsUri: string) => {
    setMetadataUri(ipfsUri);
    console.log('Metadata uploaded:', metadata, ipfsUri);
  };

  const handleContinueToConfig = () => {
    if (metadataUri) {
      // Navigate to config page with the URI as a parameter
      navigate(`/configure?uri=${encodeURIComponent(metadataUri)}`);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box mb={4}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <MuiLink
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Home
            </MuiLink>
            <Typography color="text.primary">Create Metadata</Typography>
          </Breadcrumbs>

          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h3" gutterBottom fontWeight={700}>
                Create Token Metadata
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Build and upload metadata for your ERC-1155 tokens to IPFS
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </Stack>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> Upload an image, fill in the metadata form, and publish to IPFS.
            You'll receive an IPFS URI that can be used when configuring your token.
          </Typography>
        </Alert>

        {/* Main Content */}
        <Paper elevation={0} sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
          <MetadataBuilder onComplete={handleComplete} />
        </Paper>

        {/* Next Steps */}
        {metadataUri && (
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="h6" gutterBottom>
              Next Steps
            </Typography>
            <Typography variant="body2" paragraph>
              Your metadata has been successfully uploaded to IPFS! You can now:
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleContinueToConfig}
              >
                Configure Token with This URI
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Create Another Metadata
              </Button>
            </Stack>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default CreateMetadataPage;
